import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { BadmintonMarkIcon } from "@bw/material";
import type { AccessEvaluator, NavDefinition } from "@bw/shared/navigation";
import type { FacilityServiceType } from "@bw/shared/types";

import {
  isNavItemAvailableForOrg,
  isNavItemVisibleForActions,
  type NavAccessContext,
  type NavAccessPolicy,
} from "@/config/nav-access";

export interface BusinessNavAccess extends NavAccessPolicy {
  /** Hidden at a selected facility that does not offer this service. */
  requiresService?: FacilityServiceType;
  /** Business setup: the owner of an organization that is not active yet. */
  ownerSetupOnly?: boolean;
  /** People who coach here: a coach action at the facility or a club coach profile. */
  requiresCoach?: boolean;
}

/**
 * The one business navigation source of truth (direction B, modes). Labels are
 * final names: the same text appears in the mode sub-menu, search, breadcrumbs
 * and the phone switcher. URL-only routes with no page entry: /arena,
 * /notifications, /pending and the tournament tab shims.
 */
export const businessNavigation: NavDefinition<BusinessNavAccess> = {
  app: "business",
  homePageId: "home",
  modes: [
    {
      id: "today",
      label: "Today",
      icon: TodayOutlinedIcon,
      pages: [
        {
          id: "home",
          label: "Home",
          path: "/dashboard",
          icon: HomeOutlinedIcon,
          keywords: ["overview", "dashboard"],
          access: { requiredAction: "facility:read_internal" },
          sections: [
            { id: "this-facility", label: "This facility", path: "/dashboard" },
            {
              id: "all-facilities",
              label: "All facilities",
              path: "/org",
              keywords: ["organization overview"],
              access: {
                requiresOrganizationOwner: true,
                requiredAction: "facility:read_internal",
              },
            },
          ],
        },
        {
          id: "front-desk",
          label: "Front desk",
          path: "/front-desk",
          icon: HowToRegOutlinedIcon,
          keywords: ["arrivals", "walk-in"],
          access: { requiredAction: "facility:checkin" },
          sections: [
            { id: "arrivals", label: "Arrivals", path: "/front-desk" },
            {
              id: "check-in",
              label: "Check-in",
              path: "/check-in",
              keywords: ["scan", "qr", "check in"],
            },
            {
              id: "devices",
              label: "Devices",
              path: "/front-desk/devices",
              keywords: ["kiosk", "scan kiosk", "self check-in", "tablet"],
            },
          ],
        },
        {
          id: "drop-in-live",
          label: "Drop-in live",
          path: "/services/drop-in?tab=dashboard",
          matchPaths: ["/queue"],
          icon: GroupsOutlinedIcon,
          keywords: ["queue", "court board", "open play"],
          access: {
            requiredAction: "facility:queue",
            requiresService: "open_play",
          },
        },
        {
          id: "schedule",
          label: "Schedule",
          path: "/calendar",
          icon: CalendarMonthOutlinedIcon,
          keywords: ["calendar", "daily schedule"],
          // Sections carry the gates; the page shows when one of them does.
          access: { orgAccess: "authenticated" },
          sections: [
            {
              id: "grid",
              label: "Grid",
              path: "/calendar",
              access: { requiredAction: "facility:schedule" },
            },
            {
              id: "list",
              label: "List",
              path: "/bookings",
              keywords: ["bookings", "reservations"],
              access: {
                requiredAnyActions: ["facility:schedule", "facility:manage"],
              },
            },
          ],
        },
        {
          id: "inbox",
          label: "Inbox",
          path: "/inquiries",
          icon: InboxOutlinedIcon,
          keywords: ["messages", "inquiries"],
          access: { orgAccess: "authenticated" },
        },
      ],
    },
    {
      id: "programs-events",
      label: "Programs & events",
      icon: EventNoteOutlinedIcon,
      pages: [
        {
          id: "programs",
          label: "Programs",
          path: "/programs",
          matchPaths: ["/classes", "/services/classes-coaching"],
          icon: SchoolOutlinedIcon,
          keywords: ["classes", "clinics"],
          access: { requiredAction: "facility:program" },
        },
        {
          id: "competitions",
          label: "Competitions",
          path: "/tournaments",
          icon: EmojiEventsOutlinedIcon,
          access: { orgAccess: "authenticated" },
          sections: [
            {
              id: "tournaments",
              label: "Tournaments",
              path: "/tournaments",
              // /tournaments/new and /tournaments/:id sit under this section (#2691).
              keywords: ["new tournament", "create tournament", "tournament setup", "open registration"],
              access: { requiredAction: "facility:tournament" },
            },
            {
              id: "leagues",
              label: "Leagues",
              path: "/leagues",
              access: { requiredAction: "facility:league" },
            },
          ],
        },
        {
          id: "my-coaching",
          label: "My coaching",
          path: "/coaching",
          matchPaths: ["/coaching/"],
          icon: BadmintonMarkIcon,
          keywords: ["lessons", "lesson requests", "my day"],
          inUserMenu: true,
          // Same signal as the coaching page: coach actions or a club coach profile.
          access: { orgAccess: "authenticated", requiresCoach: true },
        },
      ],
    },
    {
      id: "members-marketing",
      label: "Members & marketing",
      icon: GroupOutlinedIcon,
      pages: [
        {
          id: "memberships",
          label: "Memberships",
          path: "/memberships",
          matchPaths: ["/services/memberships"],
          icon: CardMembershipOutlinedIcon,
          keywords: ["plans", "visit cards"],
          access: { requiredAction: "facility:price" },
        },
        {
          id: "marketing",
          label: "Marketing",
          path: "/promotions",
          matchPaths: ["/memberships/promotions"],
          icon: SellOutlinedIcon,
          keywords: ["promotions", "discounts"],
          access: { requiredAction: "facility:price" },
        },
        {
          id: "community",
          label: "Community",
          path: "/community",
          icon: Diversity3OutlinedIcon,
          keywords: ["feed", "club"],
          access: { requiredAction: "facility:community" },
        },
      ],
    },
    {
      id: "money",
      label: "Money",
      icon: AccountBalanceWalletOutlinedIcon,
      pages: [
        {
          id: "payments",
          label: "Payments",
          path: "/payments",
          matchPaths: ["/setup/stripe"],
          icon: PaymentsOutlinedIcon,
          keywords: ["stripe", "disputes", "bank"],
          // Sections carry the gates (#3017): Account and Payment reports are org
          // finance data; Payouts (facility settlements) stays on read_internal and
          // the page itself gates its payout ledger and reconciliation on finance.
          access: { orgAccess: "authenticated" },
          sections: [
            {
              id: "account",
              label: "Account",
              path: "/payments",
              access: { requiresFinanceRead: true, orgAccess: "authenticated" },
            },
            {
              id: "payment-reports",
              label: "Payment reports",
              path: "/payments?tab=reports",
              access: { requiresFinanceRead: true, orgAccess: "authenticated" },
            },
            {
              id: "payouts",
              label: "Payouts",
              path: "/analytics/earnings",
              keywords: ["earnings", "settlements"],
              access: {
                requiredAction: "facility:read_internal",
                orgAccess: "approved",
              },
            },
          ],
        },
        {
          id: "reports",
          label: "Reports",
          path: "/analytics/revenue",
          matchPaths: ["/analytics"],
          icon: InsightsOutlinedIcon,
          keywords: ["insights", "analytics"],
          access: { requiredAction: "facility:read_internal" },
          sections: [
            { id: "revenue", label: "Revenue", path: "/analytics/revenue" },
            {
              id: "bookings",
              label: "Bookings",
              path: "/analytics/bookings",
              keywords: ["booking analytics"],
            },
            {
              id: "planning",
              label: "Planning",
              path: "/analytics/insights",
              keywords: ["planning insights", "demand"],
            },
            {
              id: "program-insights",
              label: "Programs",
              path: "/analytics/insights/programs",
              keywords: ["program insights"],
            },
            {
              id: "customers",
              label: "Customers",
              path: "/analytics/insights/growth",
              keywords: ["customer insights", "campaigns"],
            },
            {
              id: "instructors",
              label: "Instructors",
              path: "/analytics/insights/instructors",
              keywords: ["instructor insights"],
            },
            {
              id: "retention",
              label: "Retention",
              path: "/analytics/insights/retention",
              keywords: ["service retention"],
            },
          ],
        },
      ],
    },
    {
      id: "setup",
      label: "Setup",
      icon: TuneOutlinedIcon,
      pages: [
        {
          id: "business-setup",
          label: "Business setup",
          path: "/getting-started",
          icon: RocketLaunchOutlinedIcon,
          keywords: ["getting started", "go live"],
          access: { orgAccess: "authenticated", ownerSetupOnly: true },
        },
        {
          id: "facility",
          label: "Facility",
          path: "/settings/facility",
          matchPaths: ["/facilities/new", "/places"],
          icon: StorefrontOutlinedIcon,
          keywords: ["facility settings", "hours", "policies"],
          access: { requiredAction: "facility:manage" },
        },
        {
          id: "courts",
          label: "Courts",
          path: "/courts",
          icon: GridViewOutlinedIcon,
          access: { requiredAction: "facility:court", orgAccess: "court-setup" },
        },
        {
          id: "pricing",
          label: "Pricing",
          path: "/settings/rates",
          icon: RequestQuoteOutlinedIcon,
          keywords: ["prices"],
          access: { requiredAction: "facility:price" },
          sections: [
            { id: "rates", label: "Rates", path: "/settings/rates" },
            {
              id: "reservation-setup",
              label: "Reservation setup",
              path: "/services/court-reservation",
              keywords: ["court reservations", "booking rules"],
            },
          ],
        },
        {
          id: "drop-in-rules",
          label: "Drop-in rules",
          path: "/services/drop-in?tab=setup",
          matchPaths: ["/services/drop-in", "/settings/drop-in"],
          icon: RuleOutlinedIcon,
          keywords: ["drop-in setup", "open play"],
          access: {
            requiredAnyActions: ["facility:manage", "facility:price"],
            requiresService: "open_play",
          },
        },
        {
          id: "team",
          label: "Team",
          path: "/staff",
          icon: BadgeOutlinedIcon,
          keywords: ["staff", "roles", "people", "finance access"],
          access: { requiresTeamAccess: true, orgAccess: "staff-setup" },
        },
        {
          id: "organization",
          label: "Organization",
          path: "/settings/org",
          icon: ApartmentOutlinedIcon,
          keywords: ["org settings"],
          access: { orgAccess: "authenticated" },
          sections: [
            {
              id: "profile",
              label: "Profile",
              path: "/settings/org",
              keywords: ["organization settings"],
              access: {
                requiresOrganizationOwner: true,
                requiredAction: "facility:manage",
              },
            },
            {
              id: "platform-agreement",
              label: "Platform agreement",
              path: "/settings/contract",
              keywords: ["contract", "terms"],
              access: {
                requiresOrganizationOwner: true,
                requiredAction: "facility:manage",
              },
            },
            {
              id: "modules",
              label: "Modules",
              path: "/commercial/offers",
              matchPaths: ["/commercial"],
              keywords: ["module offers", "agreements", "readiness"],
              access: {
                requiresOrganizationOwner: true,
                orgAccess: "authenticated",
              },
            },
          ],
        },
        {
          id: "whats-enabled",
          label: "What's enabled",
          path: "/settings/access",
          icon: ToggleOnOutlinedIcon,
          keywords: ["workspace access", "capabilities"],
          // In the user menu so front desk gets no Setup tab for one link.
          // Same rule as the route (staff-setup statuses, facility selected):
          // any staff member may see what their club enabled.
          inUserMenu: true,
          access: {
            requiredAction: "facility:read",
            orgAccess: "staff-setup",
          },
        },
      ],
    },
  ],
};

export interface BusinessAccessContext extends NavAccessContext {
  orgStatus?: string;
  /** The selected facility's services; null/undefined when none is selected. */
  enabledServices?: readonly FacilityServiceType[] | null;
  organizationOwner?: boolean;
  /** Coaches here (coach action or club coach profile). Missing/loading fails closed. */
  isCoach?: boolean;
}

/** Client-side visibility only; every page and endpoint keeps its own guard. */
export function businessAccessEvaluator(
  ctx: BusinessAccessContext,
): AccessEvaluator<BusinessNavAccess> {
  return (access) => {
    const policy = access ?? {};
    if (policy.requiresCoach && ctx.isCoach !== true) return false;
    if (policy.ownerSetupOnly) {
      const setupPending =
        !ctx.orgStatus ||
        (ctx.organizationOwner === true && ctx.orgStatus !== "active");
      if (!setupPending) return false;
    }
    if (
      policy.requiresService &&
      ctx.enabledServices &&
      !ctx.enabledServices.includes(policy.requiresService)
    ) {
      return false;
    }
    if (!isNavItemAvailableForOrg(policy, ctx.orgStatus)) return false;
    return isNavItemVisibleForActions(policy, ctx);
  };
}
